import type { GQL_ID } from '@@partner:types:graphql'
import type { DeliverySlot as DeliverySlotType } from '@@partner:types:app'

import React, { useEffect, useState, useMemo } from 'react'
import { compose, head } from 'rambda'
import { mapObjIndexed } from '@@partner:helpers/util'
import { groupSlotsByDay } from '@@partner:helpers/slot'

import AppLoader from '@components/common/AppLoader'
import DeliverySlot from './DeliverySlot'

import { useFetchAvailableSlots } from '@@partner:graphql/query/order/availableSlots.gql'

import './SlotsTable.scss'

export interface renderGroupedSlotPlansProps {
  selectedSlot?: GQL_ID | null
  onSlotSelect: (slot: DeliverySlotType) => void
}

const renderGroupedSlotPlans = ({
  selectedSlot = null,
  onSlotSelect
}: renderGroupedSlotPlansProps) =>
  mapObjIndexed((slots: DeliverySlotType[], day: any) => {
    return (
      <div key={day} className="SlotsTable__Day">
        <div className="SlotsTable__Day__Title">{day}</div>
        <div className="SlotsTable__Slots">
          {slots.map((slot, index) => (
            <div className="SlotsTable__Slot" key={`slot_table-${index}`}>
              <DeliverySlot
                slot={slot}
                selected={(selectedSlot && slot.id === selectedSlot) || false}
                onSlotSelect={onSlotSelect}
              />
            </div>
          ))}
        </div>
      </div>
    )
  })

export interface SlotEvent {
  slot_id?: GQL_ID
  initial: boolean
}
export interface SlotsTableProps {
  shop_id: GQL_ID
  slotId?: GQL_ID
  onSlotSelect: (event: SlotEvent) => void
}
// Components composition
export default function SlotsTable ({
  shop_id,
  slotId,
  onSlotSelect
}: SlotsTableProps) {
  const [ selectedSlot, setSelectedSlot ] = useState<GQL_ID | undefined>(slotId)

  const { data, loading: loadingSlots } = useFetchAvailableSlots(shop_id)

  const availableSlotIds = useMemo(
    () => data?.availableSlots?.map((slot) => slot.id) ?? [],
    [ data?.availableSlots ]
  )

  useEffect(() => {
    if (!loadingSlots && data?.availableSlots && !selectedSlot) {
      const slot = head(data.availableSlots)
      if (slot) {
        setSelectedSlot(slot.id)
        onSlotSelect({ slot_id: slot.id, initial: true })
      }
    }
  }, [ data, loadingSlots, onSlotSelect, selectedSlot ])

  const handleSlotSelect = (slot: DeliverySlotType) => {
    onSlotSelect({ slot_id: slot.id, initial: false })
    setSelectedSlot(slot.id)
  }

  if (loadingSlots) {
    return <AppLoader.Inline />
  }

  return (
    <div className="SlotsTable__Container">
      {selectedSlot && !availableSlotIds.includes(selectedSlot) && (
        <span className="SlotsTable__SelectedSlotIsMissing">
          Обратите внимание - выбранный ранее слот теперь недоступен для выбора.
        </span>
      )}

      {data && data.availableSlots.length > 0 ? (
        <div className="SlotsTable">
          {compose(
            Object.values,
            renderGroupedSlotPlans({
              selectedSlot,
              onSlotSelect: handleSlotSelect
            }),
            groupSlotsByDay
          )(data.availableSlots)}
        </div>
      ) : (
        <span className="SlotsTable__NoSlots">
          Нет доступных интервалов доставки для выбранного магазина
        </span>
      )}
    </div>
  )
}
